<template>
  <wizard-shell
    :value="dialog"
    :icon="isImport ? 'mdi-table-arrow-right' : 'mdi-table-cog'"
    :title="isImport ? 'Register Import' : 'Register Schema'"
    :subtitle="register ? register.name : ''"
    :steps="shellSteps"
    :current-step="step"
    :max-width="shellMaxWidth"
    :height="shellHeight"
    :views="shellViews"
    :active-view="activeView"
    :close-disabled="isCloseDisabled"
    close-disabled-title="Locked while the register is being updated"
    close-label="Close register import"
    :progress="progress"
    :finished="isFinished"
    flush
    dialog-class="riw"
    step-variant="pill"
    @input="dialog = $event"
    @update:activeView="activeView = $event"
    @close="closeDialog">
    <template #sidebar-extras>
      <import-rail-summary :stats="railStats" :info-rows="railInfoRows" />
    </template>

    <!-- Step 1 (import): Upload -->
    <import-upload-step
      v-if="isImport && step === 1"
      :file="selectedFile"
      :processing="fileProcessing"
      :parse-error="parseError"
      :summary="fileSummary"
      :warnings="parseWarnings"
      :saved-schema-field-count="savedSchemaFieldCount"
      :show-tag-linking-note="showTagLinkingNote"
      :mode-unset="registerKind === REGISTER_KIND.GROUP_UNSET"
      :mode-first-import="registerKind === REGISTER_KIND.GROUP_EMPTY"
      :register-row-count="registerRowCount"
      @pick="onFilePicked"
      @invalid-file="onInvalidFile"
      @clear="clearFile" />

    <!--
      Mapping step. Kept mounted (v-show) while the Complete step shows the import running, so a
      failure without a server result can return here with the mapping intact.
    -->
    <field-mapping
      v-if="isImport ? step >= 2 && excelData : !!register"
      v-show="isImport ? step === 2 : step === 1"
      ref="fieldMapping"
      :excel-columns="isImport ? excelData.headers : []"
      :excel-data="isImport ? excelData.data : []"
      :register="register"
      :schema-update-mode="!isImport"
      :register-row-count="registerRowCount"
      :saved-schema-mapping="isImport ? savedSchemaMapping : null"
      :view="activeView"
      :allow-link-designation="isImport"
      hide-actions
      @status="mappingStatus = $event"
      @close="closeDialog"
      @mapping-complete="onMappingComplete"
      @proceed-to-import="isImport ? proceedToImport() : proceedToSchemaUpdate()" />

    <!-- Step 3 (import): Importing → Complete -->
    <import-complete-step
      v-if="isImport && step === 3"
      :importing="isImporting"
      :phases="importPhases"
      :phase="importPhase"
      :sent-rows="sentRows"
      :import-results="importResults"
      :register-name="register ? register.name : ''"
      :group-name="facilityGroupName"
      :facility-column="columnMappings && columnMappings.facilityColumn ? columnMappings.facilityColumn : ''"
      :mode="resultMode || ''"
      :mode-set-by-import="registerKind === REGISTER_KIND.GROUP_EMPTY ? resultMode || '' : ''" />

    <!-- Step 2 (schema update): done -->
    <div v-if="!isImport && step === 2" class="riw-panel-body">
      <div class="riw-complete">
        <div class="riw-complete__badge"><v-icon>mdi-check</v-icon></div>
        <h2 class="riw-complete__title">Schema updated successfully</h2>
        <p class="riw-complete__sub">
          The schema for “{{ register ? register.name : '' }}” now uses your field names and link designations.
        </p>
      </div>
    </div>

    <template #footer>
      <!-- Upload -->
      <template v-if="isImport && step === 1">
        <v-btn text :disabled="fileProcessing" @click="closeDialog">Cancel</v-btn>
        <span class="riw-foot-spacer"></span>
        <span v-if="registerKind === REGISTER_KIND.GROUP_UNSET" class="riw-foot-hint riw-foot-hint--err">
          <v-icon>mdi-lock-alert-outline</v-icon>An administrator must set this register’s mode
        </span>
        <v-btn color="primary" depressed :disabled="!canContinueToMapping" @click="goToMapping">
          Continue to Mapping<v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </template>

      <!-- Map Fields -->
      <template v-else-if="isImport && step === 2">
        <v-btn text :disabled="isImporting" @click="closeDialog">Cancel</v-btn>
        <span class="riw-foot-spacer"></span>
        <span v-if="footHint" class="riw-foot-hint" :class="`riw-foot-hint--${footHint.kind}`">
          <v-icon>{{ footHint.icon }}</v-icon>{{ footHint.text }}
        </span>
        <v-btn text :disabled="isImporting" @click="goBackToUpload"><v-icon left>mdi-arrow-left</v-icon>Back</v-btn>
        <v-btn color="primary" depressed :loading="isImporting" :disabled="!canSubmitMapping" @click="submitMapping">
          <v-icon left>mdi-database-import-outline</v-icon>{{ importButtonLabel }}
        </v-btn>
      </template>

      <!-- Complete -->
      <template v-else-if="isImport && step === 3">
        <span class="riw-foot-spacer"></span>
        <v-btn color="primary" depressed :disabled="isImporting" @click="closeDialog"><v-icon left>mdi-check</v-icon>Done</v-btn>
      </template>

      <!-- Schema update: Configure -->
      <template v-else-if="!isImport && step === 1">
        <v-btn text :disabled="isImporting" @click="closeDialog">Cancel</v-btn>
        <span class="riw-foot-spacer"></span>
        <span v-if="footHint && footHint.kind === 'err'" class="riw-foot-hint riw-foot-hint--err">
          <v-icon>{{ footHint.icon }}</v-icon>{{ footHint.text }}
        </span>
        <v-btn color="primary" depressed :loading="isImporting" :disabled="!canSubmitMapping" @click="submitMapping">
          <v-icon left>mdi-content-save</v-icon>Update Schema
        </v-btn>
      </template>

      <!-- Schema update: done -->
      <template v-else>
        <span class="riw-foot-spacer"></span>
        <v-btn color="primary" depressed @click="closeDialog"><v-icon left>mdi-check</v-icon>Done</v-btn>
      </template>
    </template>
  </wizard-shell>
</template>

<script>
  import { mapActions } from 'vuex';
  import WizardShell from '@/features/Share/components/wizard/WizardShell.vue';
  import { excelParser } from '../../modules/excelParser';
  import FieldMapping from './FieldMapping.vue';
  import ImportUploadStep from './importWizard/ImportUploadStep.vue';
  import ImportCompleteStep from './importWizard/ImportCompleteStep.vue';
  import ImportRailSummary from './importWizard/ImportRailSummary.vue';
  import importColumnLinksMixin from './importWizard/importColumnLinksMixin';
  import { footerHint } from './importWizard/fieldMappingPresentation';
  import { importRailInfoRows, importRailStats, importWizardSteps } from './importWizard/importRailPresentation';
  import './importWizard/styles';
  import { normalizeImportResult } from '../../modules/registerImportContracts';
  import { buildMappingFromRegisterSchema } from '../../modules/registerSavedSchemaMapping';
  import {
    REGISTER_KIND,
    isGroupRegister,
    resolveRegisterKind,
    resultingMode,
  } from '../../modules/registerImportWizardModel';
  import registerService from '../../services/registerService';
  import { log, warn, error } from '@/utils/debugLogger';

  /**
   * How long the dialog may hold itself open for an in-flight operation before it lets go.
   *
   * The close button is disabled while work is running so a half-finished import / schema update /
   * re-resolve cannot be walked away from. This dialog is `persistent`, so that button is the only
   * way out — and httpService is configured with `timeout: 0`, meaning a request that never answers
   * never rejects and would leave the flag set forever. The escape hatch keeps the lock from
   * becoming a trap: past this point the user can always leave, hung request or not.
   */
  const BUSY_CLOSE_LOCK_MS = 30000;

  /** Shell sizes (Register Import Wizard showcase): wide for mapping, compact otherwise. */
  const WIDTH_WIDE = '1350px';
  const WIDTH_DEFAULT = '1100px';
  const HEIGHT_WIDE = 'min(860px, 90vh)';
  const HEIGHT_DEFAULT = 'min(760px, calc(100vh - 140px))';
  const HEIGHT_SHORT = 'min(600px, 90vh)';

  const fmt = (value) => Number(value || 0).toLocaleString('en-US');

  export default {
    name: 'ExcelUpload',
    components: {
      WizardShell,
      FieldMapping,
      ImportUploadStep,
      ImportCompleteStep,
      ImportRailSummary,
    },
    mixins: [importColumnLinksMixin],
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      register: {
        type: Object,
        default: null,
      },
      mode: {
        type: String,
        default: 'import',
        validator: (value) => ['import', 'schema-update'].includes(value),
      },
    },
    data() {
      return {
        REGISTER_KIND,
        dialog: false,
        step: 1,
        selectedFile: null,
        uploadedFile: null,
        excelData: null,
        parseError: '',
        parseWarnings: [],
        columnMappings: {},
        importResults: null,
        fileProcessing: false,
        isProcessing: false,
        isImporting: false,
        // Which import phase is running ('writing' | 'links' | 'resolving'), for the Complete step.
        importPhase: '',
        sentRows: 0,
        // Set by the BUSY_CLOSE_LOCK_MS watchdog when an operation outstays its welcome; releases
        // the close button even though the work is still (nominally) in flight.
        closeLockExpired: false,
        closeLockTimer: null,
        // Rail Config / Preview switch on the Map Fields step (Flow A).
        activeView: 'config',
        // FieldMapping's `status` (blockers, counts, canSubmit) for the rail and the footer.
        mappingStatus: null,
        // Stored rows of an unset Facility Group register (null = unknown / not needed).
        registerRowCount: null,
        rowCountGeneration: 0,
      };
    },
    computed: {
      isImport() {
        return this.mode === 'import';
      },

      registerKind() {
        return this.isImport ? resolveRegisterKind(this.register, this.registerRowCount) : null;
      },

      facilityGroupName() {
        return (this.register && this.register.facilityGroup && this.register.facilityGroup.name) || '';
      },

      resultMode() {
        const hasColumn = !!(this.columnMappings && this.columnMappings.facilityColumn);
        return resultingMode(this.registerKind, hasColumn);
      },

      /** Mapping built from the register's saved schema — offered as a banner on Map Fields. */
      savedSchemaMapping() {
        return this.buildMappingFromRegisterSchema((this.register && this.register.schema) || []);
      },

      savedSchemaFieldCount() {
        const mapping = this.savedSchemaMapping;
        return mapping ? Object.keys(mapping.fieldMappings || {}).length : 0;
      },

      showTagLinkingNote() {
        return !!(this.register && this.register.facilityId && !this.register.facilityGroupId);
      },

      fileSummary() {
        if (!this.excelData) return null;
        return {
          rows: Array.isArray(this.excelData.data) ? this.excelData.data.length : 0,
          columns: this.excelData.headers || [],
          sheet: Array.isArray(this.excelData.sheetNames) ? this.excelData.sheetNames[0] : '',
        };
      },

      canContinueToMapping() {
        return !!this.excelData && !this.fileProcessing && this.registerKind !== REGISTER_KIND.GROUP_UNSET;
      },

      canSubmitMapping() {
        return !!(this.mappingStatus && this.mappingStatus.canSubmit) && !this.isImporting;
      },

      footHint() {
        return this.mappingStatus ? footerHint(this.mappingStatus.blockers) : null;
      },

      importButtonLabel() {
        const rows = this.mappingStatus ? this.mappingStatus.readyRows : 0;
        return `Import ${fmt(rows)} ${rows === 1 ? 'row' : 'rows'}`;
      },

      importPhases() {
        const designations = (this.columnMappings && this.columnMappings.columnLinks) || [];
        const phases = [{ key: 'writing', label: 'Writing rows to the register' }];
        if (designations.length) {
          phases.push({ key: 'links', label: 'Saving link designations' });
          phases.push({ key: 'resolving', label: 'Re-resolving links' });
        }
        return phases;
      },

      shellSteps() {
        return importWizardSteps({
          isImport: this.isImport,
          registerName: (this.register && this.register.name) || 'the register',
          fileName: this.selectedFile ? this.selectedFile.name : '',
          previewing: this.activeView === 'preview',
          importing: this.isImporting,
        });
      },

      isWideStep() {
        return this.isImport ? this.step === 2 : this.step === 1;
      },

      shellMaxWidth() {
        return this.isWideStep ? WIDTH_WIDE : WIDTH_DEFAULT;
      },

      shellHeight() {
        if (this.isWideStep) return HEIGHT_WIDE;
        if (this.isImport && this.step === 1 && !this.excelData) return HEIGHT_SHORT;
        return HEIGHT_DEFAULT;
      },

      shellViews() {
        if (!this.isImport || this.step !== 2) return [];
        const available = !!(this.mappingStatus && this.mappingStatus.previewAvailable);
        let title = null;
        if (!available) {
          title = this.registerKind === REGISTER_KIND.GROUP_UNSET
            ? 'Import is blocked until the register mode is set'
            : 'Configure at least one field to preview';
        }
        return [
          { key: 'config', label: 'Config', icon: 'mdi-tune-variant' },
          { key: 'preview', label: 'Preview', icon: 'mdi-eye-outline', disabled: !available, title },
        ];
      },

      isFinished() {
        return this.isImport ? this.step === 3 && !this.isImporting : this.step === 2;
      },

      progress() {
        if (this.isFinished) return 100;
        const total = this.shellSteps.length;
        return Math.round(((this.step - 1) / (total - 1)) * 100);
      },

      railStats() {
        return importRailStats({
          isImport: this.isImport,
          step: this.step,
          fileSummary: this.fileProcessing ? null : this.fileSummary,
          warningCount: this.parseWarnings.length,
          mappingStatus: this.mappingStatus,
        });
      },

      railInfoRows() {
        return importRailInfoRows({
          register: this.register,
          isImport: this.isImport,
          registerKind: this.registerKind,
          facilityGroupName: this.facilityGroupName,
          hasFacilityColumn: !!(this.mappingStatus && this.mappingStatus.facilityColumn),
          savedSchemaFieldCount: this.savedSchemaFieldCount,
        });
      },

      /**
       * Work is in flight that closing would abandon half-done. `isImporting` covers both the
       * import and the schema update INCLUDING the link re-resolve each one awaits, because both
       * only clear it in their `finally`.
       */
      isBusy() {
        return this.isImporting || this.isProcessing || this.fileProcessing;
      },

      /** Busy, and the watchdog has not yet handed the exit back to the user. */
      isCloseDisabled() {
        return this.isBusy && !this.closeLockExpired;
      },
    },
    methods: {
      ...mapActions('REGISTERS', {
        importData: 'IMPORT_DATA',
        updateRegisterSchema: 'UPDATE_REGISTER_SCHEMA',
      }),

      onFilePicked(file) {
        this.selectedFile = file;
        this.processFile();
      },

      onInvalidFile() {
        this.$dialog.notify.error('Please select a valid Excel or CSV file');
      },

      clearFile() {
        this.selectedFile = null;
        this.uploadedFile = null;
        this.excelData = null;
        this.parseError = '';
        this.parseWarnings = [];
        this.columnMappings = {};
      },

      goToMapping() {
        if (!this.canContinueToMapping) return;
        this.activeView = 'config';
        this.step = 2;
      },

      goBackToUpload() {
        this.activeView = 'config';
        this.step = 1;
      },

      /** Footer primary on the mapping step: the mapping step validates and emits proceed-to-import. */
      submitMapping() {
        if (!this.canSubmitMapping) return;
        this.$refs.fieldMapping?.saveMappings?.();
      },

      /**
       * Rows of an unset Facility Group register decide empty (first import sets the mode) vs.
       * populated (refused, LEG-5). RegisterVM has no row count, so read the schema's totalRows.
       * Fails open: the server refuses a populated register anyway.
       */
      async loadRegisterRowCount() {
        const generation = ++this.rowCountGeneration;
        this.registerRowCount = null;
        const reg = this.register;
        if (!this.isImport || !isGroupRegister(reg) || reg.facilityAssignmentMode) return;
        try {
          const schema = await registerService.getRegisterSchema(reg.id);
          if (generation !== this.rowCountGeneration) return;
          const total = Number(schema && schema.totalRows);
          this.registerRowCount = Number.isFinite(total) ? total : null;
          log('[RegisterImport] unset Facility Group register row count', { registerId: reg.id, rows: this.registerRowCount });
        } catch (err) {
          if (generation !== this.rowCountGeneration) return;
          warn('[RegisterImport] Could not read the register row count; the server refuses the import if the register is populated.', err);
        }
      },

      async processFile() {
        if (!this.selectedFile) return;

        this.fileProcessing = true;
        this.parseError = '';
        this.parseWarnings = [];
        this.excelData = null;

        try {
          // Parse Excel file
          const parsed = await excelParser.parse(this.selectedFile);

          log(`%c excelData`, 'color: orange; font-weight: bold; font-size: 1.5em;', parsed);

          // Validate structure
          const structureValidation = excelParser.validateStructure(parsed);
          if (!structureValidation.isValid) {
            throw new Error(structureValidation.errors.join(', '));
          }

          // Warnings (e.g. duplicate headers) are shown on the Upload step instead of as toasts.
          if (structureValidation.warnings && structureValidation.warnings.length > 0) {
            structureValidation.warnings.forEach((w) => warn('Excel validation warning:', w));
            this.parseWarnings = [...structureValidation.warnings];
          }

          // Imports preserve parsed worksheet values; schema updates keep legacy cleanup.
          if (this.mode !== 'import') {
            parsed.data = excelParser.cleanData(parsed.data);
          }

          // Auto-suggest mappings
          this.columnMappings = excelParser.suggestMappings(parsed.headers);

          this.excelData = parsed;
          this.uploadedFile = this.selectedFile;
        } catch (err) {
          // `err`, never `error`: naming it `error` shadows the imported debugLogger.error, so the
          // very first line of this block threw "error is not a function" and buried the real
          // parse failure behind it.
          error('File processing failed:', err);
          this.parseError = (err && err.message) || String(err);
        } finally {
          this.fileProcessing = false;
        }
      },

      onMappingComplete(mappings) {
        this.columnMappings = mappings;
        // Don't auto-import, let user manually proceed
      },

      proceedToImport() {
        this.performImport();
      },

      proceedToSchemaUpdate() {
        this.performSchemaUpdate();
      },

      async performImport() {
        if (
          !this.excelData ||
          !this.columnMappings ||
          Object.keys(this.columnMappings).length === 0
        ) {
          this.$dialog.notify.warning('Please complete the field mapping first');
          this.$refs.fieldMapping?.resetProcessingState?.();
          return;
        }

        const rows = Array.isArray(this.excelData.data) ? this.excelData.data : [];
        this.isImporting = true;
        this.importResults = null;
        this.importPhase = 'writing';
        this.sentRows = rows.length;
        this.activeView = 'config';
        // The Complete step shows the running import; the mapping step stays mounted (v-show).
        this.step = 3;

        try {
          const response = await this.importData({
            registerId: this.register.id,
            // Server acceptance preserves parsed worksheet rows and source text after parsing.
            data: rows,
            mapping: this.columnMappings,
            fileName: this.selectedFile?.name,
          });

          // Normalize result to expected shape
          this.importResults = normalizeImportResult(response);
          const success = !!this.importResults.success;
          const message = this.importResults.message || (success ? 'Data imported successfully' : 'Import failed');

          if (!success) {
            this.$dialog.notify.error(message);
          } else {
            this.$dialog.notify.success(message);
          }

          // Link designations can only be written once the import has created the schema — see
          // applyImportColumnLinks. Awaited while the Complete step still shows "Importing", so the
          // close lock covers it and a designation failure is reported before the result appears.
          if (success) {
            await this.applyImportColumnLinks();
          }
        } catch (err) {
          error('Import failed:', err);
          const result = err?.response?.data;
          if (result && (result.message || Array.isArray(result.errors) || Array.isArray(result.warnings) || result.statistics)) {
            // Server refusal (e.g. REGISTER_ASSIGNMENT_MODE_CONFIGURATION_REQUIRED) → shown on Complete.
            this.importResults = normalizeImportResult({
              ...result,
              errors: Array.isArray(result.errors) ? result.errors : (Array.isArray(result.warnings) ? result.warnings : []),
              success: false,
            });
          } else {
            // No server result to show: back to the mapping step, mapping intact.
            this.step = 2;
          }
          const message = err?.response?.data?.message || err?.message || 'Please try again.';
          this.$dialog.notify.error(`Import failed: ${message}`);
          // Reset the FieldMapping component's processing state
          this.$refs.fieldMapping?.resetProcessingState?.();
        } finally {
          this.isImporting = false;
          this.importPhase = '';
        }
      },

      async performSchemaUpdate() {
        if (
          !this.columnMappings ||
          Object.keys(this.columnMappings).length === 0
        ) {
          this.$dialog.notify.warning('Please complete the field mapping first');
          return;
        }

        this.isImporting = true;

        try {
          const schemaData = {
            fieldMappings: this.columnMappings.fieldMappings || {},
            fieldTypes: this.columnMappings.fieldTypes || {},
            mandatoryFields: this.columnMappings.mandatoryFields || {},
            tagColumn: this.columnMappings.tagColumn,
            facilityColumn: this.columnMappings.facilityColumn,
            identityColumn: this.columnMappings.identityColumn,
          };

          // Taken BEFORE the first await, while the mapping step is still mounted and still
          // holding the designations this save is about to replace.
          const linkBaseline = this.snapshotLinkDesignations();

          await this.updateRegisterSchema({
            registerId: this.register.id,
            schema: schemaData,
          });

          // Link designations go through their own endpoint, AFTER the rename: the schema update
          // only rewrites display names, while a designation is keyed by the internal column name.
          // Must run before `step = 2` unmounts the mapping step.
          const { saved } = await this.persistColumnLinks();

          this.$dialog.notify.success('Register schema updated successfully');

          // Writing a designation makes the SERVER drop the register's stored link resolution
          // (state -> pending), because every stored link was resolved under the designations this
          // save replaced. Nothing else rebuilds it — the register would serve the slower live
          // fallback forever — so re-resolve here, exactly as the import path does.
          //
          // `schemaData.fieldMappings` is handed over as the names this save actually wrote: a
          // RENAME invalidates the resolution too, without touching a single designation.
          await this.reResolveAfterDesignationChange(saved, linkBaseline, schemaData.fieldMappings);

          this.step = 2;

          // Emit completion event
          setTimeout(() => {
            this.$emit('complete');
          }, 2000);
        } catch (err) {
          error('Schema update failed:', err);
          this.$dialog.notify.error(`Schema update failed: ${err.message}`);
          // Reset the FieldMapping component's processing state
          this.$refs.fieldMapping?.resetProcessingState?.();
        } finally {
          this.isImporting = false;
        }
      },

      /**
       * The single way out of this dialog — the header X, and the mapping step's own Cancel, both
       * land here. Refused while work is in flight: an import, a schema update and the link
       * re-resolve that follows it all keep running after the dialog is torn down, and their result
       * would then be reported over whatever the user navigated to next.
       */
      closeDialog() {
        if (this.isCloseDisabled) {
          warn('Close refused: the register dialog is still working.', {
            registerId: this.register?.id,
            mode: this.mode,
            step: this.step,
            isImporting: this.isImporting,
            fileProcessing: this.fileProcessing,
          });
          this.$dialog.notify.info('Please wait — this register is still being updated.');
          return;
        }

        this.dialog = false;
        this.$emit('input', false);
      },

      /**
       * Start (or restart) the watchdog that eventually releases the close button. Called whenever
       * the dialog becomes busy, so each operation gets the full grace period rather than
       * inheriting what an earlier one had left.
       */
      armCloseLock() {
        this.clearCloseLockTimer();
        this.closeLockExpired = false;
        this.closeLockTimer = setTimeout(() => {
          this.closeLockTimer = null;
          this.closeLockExpired = true;
          warn('A register operation is still running after the close lock expired; releasing the dialog so the user is not stuck.', {
            registerId: this.register?.id,
            mode: this.mode,
            lockMs: BUSY_CLOSE_LOCK_MS,
          });
        }, BUSY_CLOSE_LOCK_MS);
      },

      /** The dialog went idle — drop the watchdog and re-arm the lock for the next operation. */
      releaseCloseLock() {
        this.clearCloseLockTimer();
        this.closeLockExpired = false;
      },

      clearCloseLockTimer() {
        if (this.closeLockTimer) {
          clearTimeout(this.closeLockTimer);
          this.closeLockTimer = null;
        }
      },

      resetDialog() {
        this.step = 1;
        this.selectedFile = null;
        this.uploadedFile = null;
        this.excelData = null;
        this.columnMappings = {};
        this.importResults = null;
        this.fileProcessing = false;
        this.isProcessing = false;
        this.isImporting = false;
        this.importPhase = '';
        this.sentRows = 0;
        this.parseError = '';
        this.parseWarnings = [];
        this.activeView = 'config';
        this.mappingStatus = null;
        this.releaseCloseLock();
      },

      // Build mapping compatible with FieldMapping.applyMapping from register.schema
      buildMappingFromRegisterSchema(schema = []) {
        return buildMappingFromRegisterSchema(schema);
      },

      // Load existing schema for schema update mode
      loadExistingSchema() {
        if (!this.register || !this.register.schema || this.mode !== 'schema-update') {
          return;
        }

        try {
          // Create a simulated mapping from the existing schema
          const existingMapping = this.buildMappingFromRegisterSchema(this.register.schema);
          if (existingMapping) {
            this.$nextTick(() => {
              this.$refs.fieldMapping?.applyMapping?.(existingMapping);
            });
          }
        } catch (err) {
          // Same shadowing trap as processFile: `error` as the catch binding hides the logger.
          error('Failed to load existing schema:', err);
        }
      },
    },
    beforeDestroy() {
      this.clearCloseLockTimer();
    },
    watch: {
      // The close lock follows the busy state itself, so every path that sets or clears a busy
      // flag — including the `finally` blocks and resetDialog — arms and releases it for free.
      isBusy(busy) {
        if (busy) {
          this.armCloseLock();
        } else {
          this.releaseCloseLock();
        }
      },

      // Preview needs at least one configured field (and a register that can import).
      shellViews(views) {
        const preview = views.find((view) => view.key === 'preview');
        if (this.activeView === 'preview' && (!preview || preview.disabled)) this.activeView = 'config';
      },

      value: {
        immediate: true,
        handler(newValue) {
          this.dialog = newValue;
          if (newValue && this.register) {
            // Dialog opened - reset for new upload/schema update
            this.resetDialog();
            this.loadRegisterRowCount();
            if (this.mode === 'schema-update') {
              // For schema update mode, skip to the mapping step
              this.step = 1;
              // Load existing schema from register if available
              this.loadExistingSchema();
            }
          }
        },
      },

      dialog(newValue) {
        this.$emit('input', newValue);
        if (!newValue) {
          setTimeout(() => {
            this.resetDialog();
          }, 300);
        }
      },
    },
  };
</script>
